import React, { type PropsWithChildren } from 'react';
import { defineMessages, useIntl, type MessageValue } from 'react-intl';
import { StyleSheet } from 'react-native';
import { Pressable } from 'react-native-gesture-handler';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * NOTE: We use gesture handler's pressable component instead of the Link component
 *       to prevent nested scrolling events to propagate to a press event.
 */
export const MenuCategoriesGridProductCardPressable = (
  props: MenuCategoriesGridProductCardPressableProps,
) => {
  const { productName, children, isDisabled, isLoading, onPress } = props;

  const { formatMessage } = useIntl();

  // ─────────────────────────────────────────────────────────────────────

  const a11yLabel = formatMessage(messages.a11yLabel, {
    product_name: productName,
  });

  // ─────────────────────────────────────────────────────────────────────

  const containerStyles = [styles.container, isLoading ? styles.containerLoading : undefined];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Pressable
      accessibilityRole="button"
      disabled={isDisabled}
      onPress={onPress}
      aria-label={a11yLabel}
      style={containerStyles}
    >
      {children}
    </Pressable>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{ readonly a11yLabel: { readonly product_name: MessageValue } }>({
  a11yLabel: {
    defaultMessage: 'Add 1 {product_name} to your bag',
    description: 'Menu | Categories grid | Product | Pressable card a11y label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

export const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  containerLoading: {
    opacity: 0.6,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type MenuCategoriesGridProductCardPressableProps = PropsWithChildren<{
  productName: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  onPress: () => void;
}>;
